import { useEffect, useMemo, useState, type CSSProperties, type ReactNode } from 'react';
import { useOutletContext } from 'react-router-dom';
import type { ApiClient, Projekt } from '../api';
import type {
  BefundEintrag, SimarisBaumzeile, SimarisFunktionserhalt, SimarisKabel, SimarisKnoten,
  SimarisSelektivitaetGeraet, SimarisStand, SimarisUeberlast,
} from './elektro/nea-simaris';
import {
  befundLexikon, knotenBefunde, PDF_HINWEIS, selektivText, selektivZustand, stufeZustand, zahl, zeitpunkt,
} from './elektro/nea-simaris';
import './elektro/nea-simaris.css';
import { IconHerunterladen } from '../Icons';
import { PageHeader } from '../rahmen/PageHeader';
import {
  ContentSection, DataTable, Drawer, EmptyState, ErrorState, KpiCard, KpiReihe, LoadingState, PropertyGrid,
  StatusBadge, type Spalte,
} from '../muster';

interface Rahmen { api: ApiClient; projekt: Projekt | null }

/**
 * Kurzschluss und Selektivität laut SIMARIS (EG3) — J16, 13.10.2026;
 * überarbeitet ES, 16.10.2026.
 *
 * LIEST `GET …/elektro/simaris`: den jüngsten Lauf, gegen das Modell von heute
 * und die eigene Kurzschlussrechnung gehalten, dazu die Prüfungen aus dem Text
 * (Überlastschutz, Funktionserhalt, Spannungsfall, Schaltvermögen), den
 * Netzbaum je Betriebsart und die Selektivität je Gerät. Ik-min/Zs liefert
 * SIMARIS nur im Bild (D8) — die Seite sagt das, statt zu raten.
 *
 * PDF (ELT-KS) und Excel holt erst ein Klick. Der PDF-Abruf legt nur bei geändertem
 * Stand eine neue Berichtsfassung an, sonst die abgelegte (F11); der Hinweis steht neben den Knöpfen.
 */
export function ElektroSimaris() {
  const { api, projekt } = useOutletContext<Rahmen>();
  if (!projekt) return <LoadingState />;
  return <SimarisAnsicht api={api} projektId={projekt.id} />;
}

type Reiter = 'uebersicht' | 'knoten' | 'pruefungen' | 'netzbaum' | 'selektivitaet' | 'kabel' | 'zuordnung' | 'fragen';

const REITER: [Reiter, string][] = [
  ['uebersicht', 'Übersicht'],
  ['knoten', 'Knoten'],
  ['pruefungen', 'Prüfungen'],
  ['netzbaum', 'Netzbaum'],
  ['selektivitaet', 'Selektivität'],
  ['kabel', 'Kabel und Schienen'],
  ['zuordnung', 'Zuordnung zum Modell'],
  ['fragen', 'Offene Fragen'],
];

/** Die Marken der Befunde eines Knotens. */
function Marken({ codes, lexikon }: { codes: string[]; lexikon: (c: string) => BefundEintrag }) {
  if (codes.length === 0) return <span>—</span>;
  return (
    <span className="ns-marken">
      {codes.map((c) => {
        const e = lexikon(c);
        return <StatusBadge key={c} zustand={stufeZustand(e.stufe)} text={e.text} klein />;
      })}
    </span>
  );
}

export function SimarisAnsicht({ api, projektId }: { api: ApiClient; projektId: string }) {
  const [stand, setStand] = useState<SimarisStand | null>(null);
  const [fehler, setFehler] = useState<unknown>(null);
  const [reiter, setReiter] = useState<Reiter>('uebersicht');
  const [laeuft, setLaeuft] = useState<'pdf' | 'excel' | null>(null);
  const [meldung, setMeldung] = useState<string | null>(null);

  useEffect(() => {
    let aktiv = true;
    api.elektroSimaris(projektId)
      .then((s) => { if (aktiv) { setStand(s); setFehler(null); } })
      .catch((e: unknown) => { if (aktiv) setFehler(e); });
    return () => { aktiv = false; };
  }, [api, projektId]);

  const herunterladen = async (art: 'pdf' | 'excel') => {
    setLaeuft(art);
    setMeldung(null);
    try {
      await (art === 'pdf' ? api.elektroSimarisPdf(projektId) : api.elektroSimarisExcel(projektId));
    } catch (e) {
      setMeldung(e instanceof Error ? e.message : String(e));
    } finally {
      setLaeuft(null);
    }
  };

  const kopf = (
    <PageHeader
      titel="Elektro — SIMARIS: Kurzschluss und Selektivität"
      beschreibung={'Grundlagenmappe EG3: der jüngste SIMARIS-Lauf, abgeglichen mit dem Modell und der eigenen '
        + 'Kurzschlussrechnung, mit den Prüfungen, die der Text der Quelle hergibt. Nur lesend.'}
      meta={stand ? `SIMARIS ${String(stand.lauf.simarisVersion ?? '—')} · eingelesen ${zeitpunkt(stand.lauf.eingelesenAm)}` : undefined}
      aktionen={(
        <>
          <button type="button" className="m-knopf" disabled={!stand || laeuft !== null}
                  onClick={() => herunterladen('excel')}>
            <IconHerunterladen size={13} /> Excel
          </button>
          <button type="button" className="m-knopf m-knopf-haupt" disabled={!stand || laeuft !== null}
                  onClick={() => herunterladen('pdf')}
                  title="Nachweis ELT-KS als PDF – neue Berichtsfassung nur bei geändertem Stand">
            <IconHerunterladen size={13} /> {laeuft === 'pdf' ? 'PDF wird erzeugt …' : 'Nachweis ELT-KS (PDF)'}
          </button>
        </>
      )}
    />
  );

  if (fehler) {
    const text = fehler instanceof Error ? fehler.message : String(fehler);
    const keinLauf = /404|nicht gefunden|SIMARIS-Lauf/i.test(text);
    return (
      <div className="seite">
        {kopf}
        {keinLauf
          ? <EmptyState titel="Kein SIMARIS-Lauf eingelesen"
                        erklaerung="Eingelesen wird über das Werkzeug simaris-einlesen (Projektdokumentation Netzberechnung, MS/NSHV, Selektivitätsbeurteilung)." />
          : <ErrorState titel="Der SIMARIS-Stand konnte nicht geladen werden." technisch={fehler} />}
      </div>
    );
  }

  if (!stand) return <div className="seite">{kopf}<LoadingState text="SIMARIS-Lauf wird geladen …" zeilen={8} spalten={6} /></div>;

  return (
    <div className="seite">
      {kopf}
      <p className="ns-hinweisleiste">{PDF_HINWEIS}{meldung && <StatusBadge zustand="fehler" text={meldung} />}</p>
      <Inhalt stand={stand} reiter={reiter} setReiter={setReiter} />
    </div>
  );
}

function Inhalt({ stand, reiter, setReiter }: {
  stand: SimarisStand; reiter: Reiter; setReiter: (r: Reiter) => void;
}) {
  const lexikon = useMemo(() => befundLexikon(stand.befundKatalog), [stand.befundKatalog]);
  const a = stand.abgleich;
  const ul = a.ueberlast ?? {};
  const fehlerUl = (ul.ib_ueber_iz ?? 0) + (ul.in_unter_ib ?? 0) + (ul.in_ueber_iz ?? 0);
  const fe = stand.funktionserhalt ?? [];
  const ohneFe = fe.filter((z) => !z.mitFunktionserhalt).length;
  const sel = (stand.selektivitaet ?? []) as SimarisSelektivitaetGeraet[];
  const teil = sel.filter((x) => x.gesamt === 'teilselektiv').length;

  return (
    <>
      <div className="hinweis warnung" role="note">
        <span className="hinweis-zeichen">!</span>
        <span>
          Ik-min und Schleifenimpedanz Zs laut SIMARIS: {stand.ikMinZs} (D8).
          {stand.eigeneRechnungHinweis ? ` Daneben steht die eigene Rechnung – ${stand.eigeneRechnungHinweis}.` : ''}
          {' '}Die Selektivität ist nur für die Hauptbetriebsart SV bewertet (F2).
        </span>
      </div>

      <KpiReihe>
        <KpiCard titel="SIMARIS-Verteiler" wert={stand.knoten.length}
                 vergleich={`${String(a.knotenImModell ?? 0)} im Modell`} />
        <KpiCard titel="Ik-max vergleichbar" wert={a.ikVergleichbar as number}
                 vergleich={`${String(a.ikInnerhalbZehnProzent ?? 0)} innerhalb ±10 %`} />
        <KpiCard titel="Überlastschutz Ib ≤ In ≤ Iz" wert={ul.erfuellt ?? 0} einheit="erfüllt"
                 vergleich={`${ul.ir_einstellen ?? 0} Ir einzustellen · ${fehlerUl} verletzt`}
                 zustand={fehlerUl > 0 ? 'fehler' : (ul.ir_einstellen ?? 0) > 0 ? 'warnung' : 'gut'}
                 zustandText={fehlerUl > 0 ? 'verletzt' : (ul.ir_einstellen ?? 0) > 0 ? 'Einstellung prüfen' : 'erfüllt'} />
        <KpiCard titel="Funktionserhalt an SV-Kabeln" wert={`${fe.length - ohneFe} von ${fe.length}`}
                 vergleich={`${ohneFe} ohne FE-Typ (F9)`}
                 zustand={ohneFe > 0 ? 'warnung' : 'gut'} zustandText={ohneFe > 0 ? 'prüfen' : 'mit FE'} />
        <KpiCard titel="∑Δu über ΔU max" wert={stand.spannungsfallUeberGrenze.length} einheit="Kabel"
                 vergleich={`ΔU max ${zahl(stand.netzparameter?.duMaxProzent, 1, '%')}`}
                 zustand={stand.spannungsfallUeberGrenze.length > 0 ? 'warnung' : 'gut'}
                 zustandText={stand.spannungsfallUeberGrenze.length > 0 ? 'über Grenze' : 'eingehalten'} />
        <KpiCard titel="Selektivität bewertet" wert={sel.length} einheit="Geräte"
                 vergleich={`${teil} teilselektiv`} />
      </KpiReihe>

      <div className="reiter" role="tablist" style={{ flexWrap: 'wrap' }}>
        {REITER.map(([k, t]) => (
          <button key={k} type="button" role="tab" aria-selected={reiter === k}
                  className={`reiter-knopf${reiter === k ? ' aktiv' : ''}`} onClick={() => setReiter(k)}>
            {t}
          </button>
        ))}
      </div>

      {reiter === 'uebersicht' && <Uebersicht stand={stand} />}
      {reiter === 'knoten' && <Knoten stand={stand} lexikon={lexikon} />}
      {reiter === 'pruefungen' && <Pruefungen stand={stand} lexikon={lexikon} />}
      {reiter === 'netzbaum' && <Netzbaum stand={stand} />}
      {reiter === 'selektivitaet' && <Selektivitaet zeilen={sel} />}
      {reiter === 'kabel' && <Kabel zeilen={stand.kabel ?? []} />}
      {reiter === 'zuordnung' && <Zuordnung stand={stand} />}
      {reiter === 'fragen' && <Fragen stand={stand} />}
    </>
  );
}

// ═══ Übersicht ════════════════════════════════════════════════════════════

function Uebersicht({ stand }: { stand: SimarisStand }) {
  const l = stand.lauf;
  const p = stand.netzparameter ?? {};
  const z = (l.zaehlung ?? {}) as Record<string, unknown>;
  return (
    <>
      <div className="ns-raster">
        <ContentSection titel="Lauf">
          <PropertyGrid spalten={1} dicht eintraege={[
            { beschriftung: 'SIMARIS-Version', wert: String(l.simarisVersion ?? '—') },
            { beschriftung: 'Projekt geändert', wert: String(l.projektGeaendert ?? '—') },
            { beschriftung: 'Seiten der Quelle', wert: l.seiten as number },
            { beschriftung: 'Eingelesen', wert: `${zeitpunkt(l.eingelesenAm)} (${String(l.eingelesenVon ?? '—')})` },
            { beschriftung: 'Gelesen', breit: true,
              wert: `${String(z.verbindungen ?? 0)} Verbindungen · ${String(z.geraete ?? 0)} Geräte · ${String(z.kabel ?? 0)} Kabel und Schienen · `
                + `${String(z.verteiler ?? 0)} Verteiler · ${String(z.schaltanlagen ?? 0)} Schaltanlagen · ${String(z.selektivitaetszeilen ?? 0)} Selektivitätszeilen · `
                + `${String(z.nichtZugeordnet ?? 0)} nicht zugeordnet` },
          ]} />
          {(l.dateien ?? []).length > 0 && (
            <ul className="ns-liste">
              {(l.dateien ?? []).map((d) => <li key={d}><small>{d.split(' | ')[0]}</small></li>)}
            </ul>
          )}
        </ContentSection>
        <ContentSection titel="Netzparameter">
          <PropertyGrid spalten={1} dicht eintraege={[
            { beschriftung: 'Netzsystem', wert: String(p.netzsystem ?? '—') },
            { beschriftung: 'Spannungsfaktor c max / c min NS', wert: `${zahl(p.cMaxNs, 2)} / ${zahl(p.cMinNs, 2)}` },
            { beschriftung: 'Spannungsfaktor c max / c min MS', wert: `${zahl(p.cMaxMs, 2)} / ${zahl(p.cMinMs, 2)}` },
            { beschriftung: 'ΔU max', wert: zahl(p.duMaxProzent, 1, '%'), hinweis: `Bezugspunkt: ${String(p.bezugspunktSpannungsfall ?? '—')}` },
            { beschriftung: 'Personenschutz (Wortlaut)', wert: stand.personenschutz ?? '—', breit: true },
          ]} />
        </ContentSection>
      </div>

      <ContentSection titel="Einspeisungen">
        <DataTable suche={false} spaltenwahl={false} dicht zeilen={stand.einspeisungen}
                   schluessel={(e) => e.bezeichnung}
                   spalten={[
                     { schluessel: 'bezeichnung', titel: 'Einspeisung', wert: (e) => e.bezeichnung },
                     { schluessel: 'art', titel: 'Art', wert: (e) => (e.art === 'ms_netz' ? 'MS-Netz' : 'Generator (Ik3D)') },
                     { schluessel: 'ikMax', titel: 'Ik max', einheit: 'kA', art: 'zahl', nachkomma: 3, wert: (e) => e.ikMaxKa },
                     { schluessel: 'ikMin', titel: 'Ik min', einheit: 'kA', art: 'zahl', nachkomma: 3, wert: (e) => e.ikMinKa },
                     { schluessel: 'seite', titel: 'Seite', art: 'zahl', nachkomma: 0, wert: (e) => e.seite },
                   ]} />
      </ContentSection>

      <ContentSection titel="Netzbetriebsarten" beschreibung="Je Betriebsart die Verbindungen der Netzberechnung und wie viele davon eingeschaltet sind.">
        <DataTable suche={false} spaltenwahl={false} dicht zeilen={stand.betriebsarten ?? []}
                   schluessel={(b) => b.name}
                   spalten={[
                     { schluessel: 'name', titel: 'Netzbetriebsart', wert: (b) => b.name },
                     { schluessel: 'zustand', titel: 'Zustand', wert: (b) => (b.deaktiviert ? 'deaktiviert' : 'aktiv'),
                       inhalt: (b) => <StatusBadge zustand={b.deaktiviert ? 'neutral' : 'gut'} text={b.deaktiviert ? 'deaktiviert' : 'aktiv'} klein /> },
                     { schluessel: 'verbindungen', titel: 'Verbindungen', art: 'zahl', nachkomma: 0, wert: (b) => b.verbindungen },
                     { schluessel: 'ein', titel: 'davon ein', art: 'zahl', nachkomma: 0, wert: (b) => b.ein },
                   ]} />
      </ContentSection>
    </>
  );
}

// ═══ Knoten ═══════════════════════════════════════════════════════════════

function Knoten({ stand, lexikon }: { stand: SimarisStand; lexikon: (c: string) => BefundEintrag }) {
  const [filter, setFilter] = useState<string>('befund');
  const [gewaehlt, setGewaehlt] = useState<SimarisKnoten | null>(null);

  const codes = useMemo(() => [...new Set(stand.knoten.flatMap((k) => knotenBefunde(k.befunde)))].sort(), [stand.knoten]);

  const zeilen = useMemo(() => stand.knoten.filter((k) => {
    const b = knotenBefunde(k.befunde);
    if (filter === 'alle') return true;
    if (filter === 'befund') return b.some((c) => c !== 'nicht_im_modell');
    if (filter === 'modell') return k.modellVerteilerId !== null;
    return b.includes(filter);
  }), [stand.knoten, filter]);

  const spalten: Spalte<SimarisKnoten>[] = [
    { schluessel: 'name', titel: 'Knoten (SIMARIS)', wert: (k) => k.name, immerSichtbar: true },
    { schluessel: 'modell', titel: 'Modell', wert: (k) => k.modellKennzeichen ?? '—' },
    { schluessel: 'ikQuelle', titel: 'Ik-max SIMARIS', einheit: 'kA', art: 'zahl', nachkomma: 2, wert: (k) => k.ikMaxQuelleKa },
    { schluessel: 'ikEigen', titel: 'Ik-max eigen', einheit: 'kA', art: 'zahl', nachkomma: 2, wert: (k) => k.eigen?.ik3MaxKa ?? k.ikModellKa },
    { schluessel: 'ikAbw', titel: 'Abweichung', einheit: '%', art: 'zahl', nachkomma: 1, wert: (k) => k.ikAbweichungProzent },
    { schluessel: 'ikMin', titel: 'Ik-min eigen', einheit: 'kA', art: 'zahl', nachkomma: 2, wert: (k) => k.eigen?.ik1MinKa ?? null },
    { schluessel: 'zs', titel: 'Zs eigen', einheit: 'mΩ', art: 'zahl', nachkomma: 1, wert: (k) => k.eigen?.zsMOhm ?? null, versteckt: true },
    { schluessel: 'icw', titel: 'Icw', einheit: 'kA', art: 'zahl', nachkomma: 0, wert: (k) => k.icwKa },
    { schluessel: 'zuleitung', titel: 'Zuleitung (SIMARIS)', wert: (k) => k.zuleitungQuerschnittText
        ? `${k.zuleitungAnzahlKabel && k.zuleitungAnzahlKabel > 1 ? `${k.zuleitungAnzahlKabel} × ` : ''}${k.zuleitungQuerschnittText}` : '—' },
    { schluessel: 'befunde', titel: 'Befunde', wert: (k) => knotenBefunde(k.befunde).map((c) => lexikon(c).text).join(' · '),
      inhalt: (k) => <Marken codes={knotenBefunde(k.befunde)} lexikon={lexikon} /> },
  ];

  return (
    <ContentSection
      titel="Knoten: SIMARIS gegen Modell und eigene Rechnung"
      beschreibung="Ik-max SIMARIS = gefordertes Schaltvermögen der Geräte am Knoten (Icu/Icn erforderlich). Eine Zeile anklicken für die Einzelheiten."
      werkzeuge={(
        <label className="ns-werkzeuge">
          Anzeigen
          <select className="m-feld-eingabe" value={filter} onChange={(e) => setFilter(e.target.value)}>
            <option value="befund">mit Abweichung</option>
            <option value="modell">im Modell zugeordnet</option>
            <option value="alle">alle Knoten</option>
            {codes.map((c) => <option key={c} value={c}>{lexikon(c).text}</option>)}
          </select>
        </label>
      )}>
      <DataTable name={`${zeilen.length} von ${stand.knoten.length} Knoten`} spalten={spalten} zeilen={zeilen}
                 schluessel={(k) => `${k.name}-${k.schluessel ?? ''}`} dicht
                 beiZeileKlick={setGewaehlt} aktiveZeile={gewaehlt ? `${gewaehlt.name}-${gewaehlt.schluessel ?? ''}` : undefined}
                 ausfuhrDateiname="SIMARIS_Knoten" />
      <KnotenDetail knoten={gewaehlt} stand={stand} lexikon={lexikon} beiSchliessen={() => setGewaehlt(null)} />
    </ContentSection>
  );
}

function KnotenDetail({ knoten: k, stand, lexikon, beiSchliessen }: {
  knoten: SimarisKnoten | null; stand: SimarisStand; lexikon: (c: string) => BefundEintrag; beiSchliessen: () => void;
}) {
  const kabel = k?.zuleitungKabel ? (stand.kabel ?? []).find((x) => x.bezeichnung === k.zuleitungKabel) : undefined;
  return (
    <Drawer offen={k !== null} titel={k?.name ?? ''} beschreibung={k?.modellKennzeichen ? `Modell: ${k.modellKennzeichen} (${k.zuordnung ?? '—'})` : 'nicht im Modell'}
            beiSchliessen={beiSchliessen} breite="560px">
      {k && (
        <>
          <h4>Kurzschluss</h4>
          <PropertyGrid spalten={2} dicht eintraege={[
            { beschriftung: 'Ik-max SIMARIS', wert: k.ikMaxQuelleKa, einheit: 'kA', nachkomma: 2, hinweis: 'gefordertes Schaltvermögen am Knoten' },
            { beschriftung: 'Ik-max eigen', wert: k.eigen?.ik3MaxKa ?? k.ikModellKa, einheit: 'kA', nachkomma: 2, herkunft: 'modell',
              hinweis: k.eigen?.herkunft ?? k.ikModellHerkunft ?? undefined },
            { beschriftung: 'Abweichung', wert: k.ikAbweichungProzent, einheit: '%', nachkomma: 1 },
            { beschriftung: 'Ik-min eigen', wert: k.eigen?.ik1MinKa ?? null, einheit: 'kA', nachkomma: 2, herkunft: 'modell' },
            { beschriftung: 'Zs eigen', wert: k.eigen?.zsMOhm ?? null, einheit: 'mΩ', nachkomma: 1, herkunft: 'modell' },
            { beschriftung: 'Ik-min / Zs SIMARIS', wert: stand.ikMinZs, breit: true },
            { beschriftung: 'Icw Sammelschiene', wert: k.icwKa, einheit: 'kA', nachkomma: 0, hinweis: k.icwModellKa ? `Modell ${zahl(k.icwModellKa, 0, 'kA')}` : 'im Modell leer' },
          ]} />
          {(k.ikGeraete ?? []).length > 0 && (
            <>
              <h4>Geräte mit gefordertem Schaltvermögen</h4>
              <ul className="ns-liste">{(k.ikGeraete ?? []).map((g) => <li key={g}>{g}</li>)}</ul>
            </>
          )}
          <h4>Zuleitung</h4>
          <PropertyGrid spalten={2} dicht eintraege={[
            { beschriftung: 'Verbindung', wert: k.zuleitung ?? '—' },
            { beschriftung: 'Kabel', wert: k.zuleitungKabel ?? '—' },
            { beschriftung: 'Querschnitt SIMARIS', wert: k.zuleitungQuerschnittText ?? '—',
              hinweis: k.zuleitungAnzahlKabel && k.zuleitungAnzahlKabel > 1 ? `${k.zuleitungAnzahlKabel} Kabel parallel` : undefined },
            { beschriftung: 'Querschnitt Modell', wert: k.zuleitungQuerschnittModellMm2, einheit: 'mm²', nachkomma: k.zuleitungQuerschnittModellMm2 !== null && k.zuleitungQuerschnittModellMm2 < 10 ? 1 : 0,
              herkunft: 'modell', hinweis: k.zuleitungModellHerkunft ?? undefined },
            { beschriftung: 'Länge SIMARIS', wert: k.zuleitungLaengeQuelleM ?? null, einheit: 'm', nachkomma: 0 },
            { beschriftung: 'Länge Modell', wert: k.zuleitungLaengeModellM ?? null, einheit: 'm', nachkomma: 1, herkunft: 'modell' },
            ...(kabel ? [
              { beschriftung: 'Typ', wert: kabel.typ ?? '—' },
              { beschriftung: 'Verlegeart / fges', wert: `${kabel.verlegeart ?? '—'} / ${zahl(kabel.fges, 2)}` },
              { beschriftung: 'Ib / Iz', wert: `${zahl(kabel.ibA, 1)} / ${zahl(kabel.izA, 1)} A` },
              { beschriftung: 'Δu / ∑Δu', wert: `${zahl(kabel.duProzent, 3)} / ${zahl(kabel.summeDuProzent, 3)} %` },
            ] : []),
            { beschriftung: 'Schutzgerät', wert: k.zuleitungsschutz ?? '—', breit: true },
          ]} />
          <h4>Selektivität (Hauptbetriebsart SV)</h4>
          <span className="ns-marken">
            {([['Ik3/Ik2', k.selektivitaetIk3], ['Ik1-PE', k.selektivitaetIk1Pe], ['Ik1-N', k.selektivitaetIk1N]] as [string, string | null | undefined][])
              .map(([n, b]) => <StatusBadge key={n} zustand={selektivZustand(b)} text={`${n}: ${selektivText(b)}`} klein />)}
          </span>
          {k.selektivitaetSeite ? <p><small>Selektivitätsbeurteilung Seite {k.selektivitaetSeite}</small></p> : null}
          <h4>Befunde</h4>
          <Marken codes={knotenBefunde(k.befunde)} lexikon={lexikon} />
        </>
      )}
    </Drawer>
  );
}

// ═══ Prüfungen ════════════════════════════════════════════════════════════

function Pruefungen({ stand, lexikon }: { stand: SimarisStand; lexikon: (c: string) => BefundEintrag }) {
  const [alle, setAlle] = useState(false);
  const ul = (stand.ueberlast ?? []).filter((z) => alle || z.bewertung !== 'erfuellt');

  const ueberlast: Spalte<SimarisUeberlast>[] = [
    { schluessel: 'kabel', titel: 'Kabel', wert: (z) => z.kabel, immerSichtbar: true },
    { schluessel: 'startZiel', titel: 'Start / Ziel', wert: (z) => z.startZiel },
    { schluessel: 'schutz', titel: 'Schutzgerät', wert: (z) => (z.schutzgeraet ? `${z.schutzgeraet}${z.ausloeser ? ` (${z.ausloeser})` : ''}` : '—') },
    { schluessel: 'ib', titel: 'Ib', einheit: 'A', art: 'zahl', nachkomma: 1, wert: (z) => z.ibA },
    { schluessel: 'in', titel: 'In', einheit: 'A', art: 'zahl', nachkomma: 0, wert: (z) => z.inA },
    { schluessel: 'iz', titel: 'Iz', einheit: 'A', art: 'zahl', nachkomma: 1, wert: (z) => z.izA },
    { schluessel: 'bewertung', titel: 'Bewertung', wert: (z) => lexikon(z.bewertung).text,
      inhalt: (z) => (
        <StatusBadge klein zustand={z.bewertung === 'erfuellt' ? 'gut' : stufeZustand(lexikon(z.bewertung).stufe)}
                     text={z.irVon !== null && z.irBis !== null
                       ? `Ir einstellen: ${zahl(z.irVon, 0)} … ${zahl(z.irBis, 0)} A` : lexikon(z.bewertung).text} />
      ) },
  ];

  const funktion: Spalte<SimarisFunktionserhalt>[] = [
    { schluessel: 'kabel', titel: 'Kabel', wert: (z) => z.kabel },
    { schluessel: 'von', titel: 'von', wert: (z) => z.von },
    { schluessel: 'nach', titel: 'nach', wert: (z) => z.nach },
    { schluessel: 'typ', titel: 'Typ', wert: (z) => z.typ ?? '—' },
    { schluessel: 'fe', titel: 'Funktionserhalt', wert: (z) => (z.mitFunktionserhalt ? z.funktionserhalt ?? 'FE' : 'kein FE-Typ'),
      inhalt: (z) => <StatusBadge klein zustand={z.mitFunktionserhalt ? 'gut' : 'warnung'} text={z.mitFunktionserhalt ? (z.funktionserhalt ?? 'FE').replace(' integriert', '') : 'kein FE-Typ'} /> },
    { schluessel: 'bezug', titel: 'Bezug', wert: (z) => lexikon(z.bezug).text },
  ];

  return (
    <>
      <ContentSection
        titel="Überlastschutz Ib ≤ In ≤ Iz (DIN VDE 0100-430)"
        beschreibung={'Werte der Quelle. In ist bei Leistungsschaltern die Baugröße, nicht der Einstellwert Ir – der steht nicht im Text (F10). '
          + 'Bei Schienen gilt der Bemessungsstrom des Systems als Iz. MS-Kabel sind ausgenommen (Schutz über Relais).'}
        werkzeuge={(
          <label className="ns-werkzeuge">
            <input type="checkbox" checked={alle} onChange={(e) => setAlle(e.target.checked)} /> auch erfüllte zeigen
          </label>
        )}>
        <DataTable name={`${ul.length} Kabel und Schienen`} spalten={ueberlast} zeilen={ul} dicht
                   schluessel={(z) => `${z.kabel}-${z.seite}`} ausfuhrDateiname="SIMARIS_Ueberlastschutz"
                   leer="Alle Kabel und Schienen erfüllen Ib ≤ In ≤ Iz mit den Werten der Quelle." />
      </ContentSection>

      <ContentSection
        titel="Funktionserhalt an Kabeln der Sicherheitsstromversorgung"
        beschreibung={'Kabel ab einer NEA oder einer SV-Verteilung und Kabel zu einer SV-Verteilung. „kein FE-Typ“ heißt nicht „falsch“: '
          + 'Verlegung (eigener Raum oder Brandabschnitt, MLAR 5.3) oder eine eigene Batterie (CPS, USV) können ihn erbringen (F9).'}>
        <DataTable name={`${(stand.funktionserhalt ?? []).length} Kabel`} spalten={funktion} zeilen={stand.funktionserhalt ?? []} dicht
                   schluessel={(z) => `${z.kabel}-${z.seite}`} ausfuhrDateiname="SIMARIS_Funktionserhalt" />
      </ContentSection>

      <ContentSection titel={`Spannungsfall über ΔU max (${zahl(stand.netzparameter?.duMaxProzent, 1, '%')})`}
                      beschreibung="Bezugspunkt laut SIMARIS: die Transformator-Sekundärklemmen. Für Abgänge im NEA-Betrieb wäre die NEA der Bezug (F5).">
        <DataTable suche={false} dicht zeilen={stand.spannungsfallUeberGrenze}
                   schluessel={(z) => String(z.bezeichnung)}
                   leer="Kein Kabel über der Grenze."
                   spalten={[
                     { schluessel: 'bezeichnung', titel: 'Kabel', wert: (z) => z.bezeichnung },
                     { schluessel: 'startZiel', titel: 'Start / Ziel', wert: (z) => z.startZiel },
                     { schluessel: 'q', titel: 'Querschnitt', wert: (z) => z.querschnittText ?? '—' },
                     { schluessel: 'laenge', titel: 'Länge', einheit: 'm', art: 'zahl', nachkomma: 0, wert: (z) => z.laengeM },
                     { schluessel: 'du', titel: 'Δu', einheit: '%', art: 'zahl', nachkomma: 3, wert: (z) => z.duProzent },
                     { schluessel: 'sdu', titel: '∑Δu', einheit: '%', art: 'zahl', nachkomma: 3, wert: (z) => z.summeDuProzent },
                     { schluessel: 'seite', titel: 'Seite', art: 'zahl', nachkomma: 0, wert: (z) => z.seite },
                   ] as Spalte<Record<string, unknown>>[]} />
      </ContentSection>

      <ContentSection titel="Schaltvermögen Icu ≥ Ik-max am Einbauort">
        <DataTable suche={false} dicht zeilen={stand.schaltvermoegenZuKlein ?? []}
                   schluessel={(z) => String(z.bezeichnung)}
                   leer="Bei allen Geräten mit Angabe ist Icu mindestens so groß wie das geforderte Schaltvermögen."
                   spalten={[
                     { schluessel: 'bezeichnung', titel: 'Gerät', wert: (z) => z.bezeichnung },
                     { schluessel: 'ort', titel: 'Ort', wert: (z) => z.ort },
                     { schluessel: 'in', titel: 'In', einheit: 'A', art: 'zahl', nachkomma: 0, wert: (z) => z.inA },
                     { schluessel: 'icu', titel: 'Icu', einheit: 'kA', art: 'zahl', nachkomma: 1, wert: (z) => z.icuKa },
                     { schluessel: 'erf', titel: 'gefordert', einheit: 'kA', art: 'zahl', nachkomma: 1, wert: (z) => z.icuErforderlichKa },
                   ] as Spalte<Record<string, unknown>>[]} />
      </ContentSection>
    </>
  );
}

// ═══ Netzbaum ═════════════════════════════════════════════════════════════

function Netzbaum({ stand }: { stand: SimarisStand }) {
  const arten = stand.betriebsarten ?? [];
  const [art, setArt] = useState<string>(() => arten.find((b) => b.name === 'SV' && !b.deaktiviert)?.name
    ?? arten.find((b) => !b.deaktiviert)?.name ?? arten[0]?.name ?? '');
  const zeilen = stand.netzbaum?.[art] ?? [];

  const spalten: Spalte<SimarisBaumzeile & { nr: number }>[] = [
    { schluessel: 'knoten', titel: 'Knoten', sortierbar: false, wert: (z) => z.knoten,
      inhalt: (z) => (
        <span className={`ns-einzug${z.ebene === 0 ? ' ns-einzug-wurzel' : ''}${z.wiederholung ? ' ns-einzug-wiederholung' : ''}`}
              style={{ '--ns-ebene': z.ebene } as CSSProperties}>
          {z.ebene > 0 ? '› ' : ''}{z.knoten}{z.wiederholung ? ' (siehe oben)' : ''}
        </span>
      ) },
    { schluessel: 'verbindung', titel: 'über Verbindung', sortierbar: false, wert: (z) => z.verbindung ?? '' },
    { schluessel: 'kabel', titel: 'Kabel / Schiene', sortierbar: false, wert: (z) => z.kabeltext ?? '' },
    { schluessel: 'schutz', titel: 'Schutzgerät', sortierbar: false, wert: (z) => z.schutz ?? '' },
  ];

  return (
    <ContentSection
      titel="Netzbaum je Netzbetriebsart"
      beschreibung="Nur eingeschaltete Verbindungen. „(siehe oben)“: Der Knoten ist schon über eine andere Verbindung erreicht (Kupplung, Ring)."
      werkzeuge={(
        <label className="ns-werkzeuge">
          Betriebsart
          <select className="m-feld-eingabe" value={art} onChange={(e) => setArt(e.target.value)}>
            {arten.map((b) => <option key={b.name} value={b.name}>{b.name}{b.deaktiviert ? ' (deaktiviert)' : ''}</option>)}
          </select>
        </label>
      )}>
      <DataTable name={`${zeilen.length} Knoten`} dicht spalten={spalten}
                 zeilen={zeilen.map((z, i) => ({ ...z, nr: i }))} schluessel={(z) => String(z.nr)}
                 ausfuhrDateiname={`SIMARIS_Netzbaum_${art}`} />
    </ContentSection>
  );
}

// ═══ Selektivität, Kabel, Zuordnung, Fragen ═══════════════════════════════

function Selektivitaet({ zeilen }: { zeilen: SimarisSelektivitaetGeraet[] }) {
  const marke = (b: string | null): ReactNode => <StatusBadge klein zustand={selektivZustand(b)} text={selektivText(b)} />;
  return (
    <ContentSection titel="Selektivität je Gerät (Hauptbetriebsart SV)"
                    beschreibung="Blätter ohne Bewertung (Gerät ohne nachgeordnetes Gerät im SIMARIS-Modell) fehlen – ohne Bewertung heißt nicht „selektiv“. Die Grenze einer Teilselektivität steht nur im Kennlinienbild (D8).">
      <DataTable name={`${zeilen.length} Geräte`} dicht zeilen={zeilen} schluessel={(x) => x.geraet}
                 ausfuhrDateiname="SIMARIS_Selektivitaet"
                 spalten={[
                   { schluessel: 'geraet', titel: 'Gerät', wert: (x) => x.geraet },
                   { schluessel: 'kreis', titel: 'Stromkreis', wert: (x) => x.kreis },
                   { schluessel: 'ik3', titel: 'Ik3 / Ik2', wert: (x) => selektivText(x.ik3Ik2), inhalt: (x) => marke(x.ik3Ik2) },
                   { schluessel: 'ik1pe', titel: 'Ik1-PE', wert: (x) => selektivText(x.ik1Pe), inhalt: (x) => marke(x.ik1Pe) },
                   { schluessel: 'ik1n', titel: 'Ik1-N', wert: (x) => selektivText(x.ik1N), inhalt: (x) => marke(x.ik1N) },
                   { schluessel: 'seiten', titel: 'Seiten', wert: (x) => x.seiten.join(', ') },
                 ]} />
    </ContentSection>
  );
}

function Kabel({ zeilen }: { zeilen: SimarisKabel[] }) {
  const art: Record<string, string> = { ms: 'MS', ns: 'NS', fe: 'NS mit FE', schiene: 'Schiene' };
  return (
    <ContentSection titel="Kabel und Schienen laut SIMARIS" beschreibung="Iz schon mit fges und Anzahl; bei Schienen der Bemessungsstrom des Systems.">
      <DataTable name={`${zeilen.length} Kabel und Schienen`} dicht zeilen={zeilen} proSeite={50}
                 schluessel={(k) => `${k.bezeichnung}-${k.seite}`} ausfuhrDateiname="SIMARIS_Kabel"
                 spalten={[
                   { schluessel: 'bezeichnung', titel: 'Kabel', wert: (k) => k.bezeichnung, immerSichtbar: true },
                   { schluessel: 'art', titel: 'Art', wert: (k) => art[k.art] ?? k.art },
                   { schluessel: 'q', titel: 'Querschnitt', wert: (k) => `${k.anzahl && k.anzahl > 1 ? `${k.anzahl} × ` : ''}${k.querschnittText ?? k.typ ?? '—'}` },
                   { schluessel: 'typ', titel: 'Typ', wert: (k) => k.typ ?? '—', versteckt: true },
                   { schluessel: 'startZiel', titel: 'Start / Ziel', wert: (k) => k.startZiel },
                   { schluessel: 'laenge', titel: 'Länge', einheit: 'm', art: 'zahl', nachkomma: 0, wert: (k) => k.laengeM },
                   { schluessel: 'verlegeart', titel: 'Verlegeart', wert: (k) => k.verlegeart ?? '—', versteckt: true },
                   { schluessel: 'fges', titel: 'fges', art: 'zahl', nachkomma: 2, wert: (k) => k.fges, versteckt: true },
                   { schluessel: 'ib', titel: 'Ib', einheit: 'A', art: 'zahl', nachkomma: 1, wert: (k) => k.ibA },
                   { schluessel: 'iz', titel: 'Iz', einheit: 'A', art: 'zahl', nachkomma: 1, wert: (k) => k.izA },
                   { schluessel: 'du', titel: 'Δu', einheit: '%', art: 'zahl', nachkomma: 3, wert: (k) => k.duProzent, versteckt: true },
                   { schluessel: 'sdu', titel: '∑Δu', einheit: '%', art: 'zahl', nachkomma: 3, wert: (k) => k.summeDuProzent },
                   { schluessel: 'fe', titel: 'Funktionserhalt', wert: (k) => (k.funktionserhalt ?? '').replace(' integriert', '') },
                   { schluessel: 'seite', titel: 'Seite', art: 'zahl', nachkomma: 0, wert: (k) => k.seite, versteckt: true },
                 ]} />
    </ContentSection>
  );
}

function Zuordnung({ stand }: { stand: SimarisStand }) {
  const ohne = stand.knoten.filter((k) => k.modellVerteilerId === null);
  const modell = stand.modellOhneQuelle as { kennzeichen: string; art: string; versorgungsart: string }[];
  return (
    <div className="ns-raster">
      <ContentSection titel={`SIMARIS-Knoten ohne Modellverteiler (${ohne.length})`}
                      beschreibung="Zugeordnet wird über den normalisierten Namen (UV_, SV_, NSHV_, IT_) oder eine bestätigte Festlegung. MS, Trafo, GHV und NSHV_SV heißen im Modell anders (F6).">
        <DataTable dicht suche zeilen={ohne} schluessel={(k) => k.name}
                   spalten={[
                     { schluessel: 'name', titel: 'Knoten', wert: (k) => k.name },
                     { schluessel: 'spannung', titel: 'Spannung', wert: (k) => k.spannung ?? '—' },
                     { schluessel: 'ik', titel: 'Ik-max', einheit: 'kA', art: 'zahl', nachkomma: 2, wert: (k) => k.ikMaxQuelleKa },
                   ]} />
      </ContentSection>
      <ContentSection titel={`Modellverteiler ohne SIMARIS-Gegenstück (${modell.length})`}>
        <DataTable dicht suche zeilen={modell} schluessel={(m) => m.kennzeichen}
                   spalten={[
                     { schluessel: 'kennzeichen', titel: 'Kennzeichen', wert: (m) => m.kennzeichen },
                     { schluessel: 'art', titel: 'Art', wert: (m) => m.art },
                     { schluessel: 'versorgung', titel: 'Versorgung', wert: (m) => m.versorgungsart },
                   ]} />
      </ContentSection>
    </div>
  );
}

function Fragen({ stand }: { stand: SimarisStand }) {
  return (
    <ContentSection titel={`Offene Fragen an die Elektroplanung (${stand.offeneFragen.length})`}>
      <ul className="ns-liste">
        {stand.offeneFragen.map((f) => <li key={f.nr}><strong>{f.nr}</strong> {f.frage}</li>)}
      </ul>
    </ContentSection>
  );
}
